import { guardDialogFocus, trapTabFocus } from '../modules/composer-dialog.js';

/**
 * Anchored reply composer. A section affordance carries its anchor in data-* attributes, and
 * the composer sends an ordinary PM with a profile_ref attachment through POST /v1/message.
 * Ids stay strings, because they exceed Number.MAX_SAFE_INTEGER.
 */

function readAnchorFromButton(btn) {
    return {
        kind: btn.dataset.anchorKind || '',
        label: btn.dataset.anchorLabel || '',
        excerpt: btn.dataset.anchorExcerpt || '',
        entityId: btn.dataset.anchorEntityId || '',
        lang: btn.dataset.anchorLang || '',
        thumb: btn.dataset.anchorThumb || '',
    };
}

document.addEventListener('click', (e) => {
    const btn = e.target.closest('.js-anchor-reply');
    if (!btn) {
        return;
    }
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('anchor-reply:open', { detail: { ...readAnchorFromButton(btn), trigger: btn } }));
});

// Must stay above request_terminate_timeout in docker/conf/usr/local/etc/php-fpm.conf.
const SEND_TIMEOUT_MS = 35000;
const FAILURE_TOAST_MS = 15000;

/**
 * POST an anchored private message. The anchored content is always the recipient's own
 * profile, so the anchor owner is the recipient.
 *
 * @returns {Promise<Response>}
 */
function postAnchoredMessage({ recipientId, kind, entityId, lang, excerpt, body, tmpId, signal }) {
    const fd = new FormData();
    fd.append('tmp_id', tmpId);
    fd.append('target_id', recipientId);
    fd.append('message', body);
    fd.append('attachment_type', 'profile_ref');
    fd.append('anchor_kind', kind);
    fd.append('anchor_owner_uid', recipientId);
    if (entityId) {
        fd.append('anchor_entity_id', entityId);
    }
    if (lang) {
        fd.append('anchor_lang', lang);
    }
    // Only a text section stores its excerpt. The server refuses the send when that text changed.
    if (excerpt && !entityId && !lang) {
        fd.append('anchor_excerpt', excerpt);
    }

    return window.ApiClient.request('/message', { method: 'POST', body: fd, signal });
}

// The deadline covers the token refresh of ApiClient and the read of the response body.
function withSendDeadline(work) {
    const controller = new AbortController();
    let timer;
    const deadline = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
            controller.abort();
            reject(new Error('anchored reply send timed out'));
        }, SEND_TIMEOUT_MS);
    });
    return Promise.race([work(controller.signal), deadline])
        .finally(() => clearTimeout(timer));
}

/**
 * Extract a human-readable string from the API error envelope
 * ({ error: { message, errors: { field: { message } } } }). Only ever returns a string so
 * x-text never renders "[object Object]".
 */
async function anchoredSendErrorMessage(res, fallback, changedText) {
    try {
        const data = await res.json();
        const err = data && data.error;
        const fieldErrors = err && err.errors;
        // The composer sends only anchors the page rendered, so an anchor error means the section changed.
        if (fieldErrors && typeof fieldErrors === 'object' && changedText
            && Object.keys(fieldErrors).some((field) => field.startsWith('anchor_'))) {
            return changedText;
        }
        if (fieldErrors && typeof fieldErrors === 'object') {
            const first = Object.values(fieldErrors)[0];
            if (first && typeof first.message === 'string' && first.message) {
                return first.message;
            }
        }
        if (err && typeof err.message === 'string' && err.message) {
            return err.message;
        }
        if (data && typeof data.message === 'string' && data.message) {
            return data.message;
        }
    } catch {
        // The body is not JSON, so the generic message applies.
    }
    return fallback;
}

function newTmpId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID().replace(/-/g, '');
    }
    return (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 32);
}

function anchorKey(anchor) {
    return `${anchor.kind}|${anchor.entityId}|${anchor.lang}`;
}

// :focus-visible also marks focus that a script moved after a key press, so it tells a keyboard user from a pointer user.
function hasKeyboardFocus(el) {
    try {
        return !!el && document.activeElement === el && el.matches(':focus-visible');
    } catch {
        return false;
    }
}

// The React photo viewer field opens the sheet through the open event, so it has no .js-anchor-reply class.
const REPLY_BUTTON_SELECTOR = '.js-anchor-reply, [data-react-photo-private-reply]';

function hasKeyboardFocusOnReplyButton() {
    const el = document.activeElement;
    return hasKeyboardFocus(el) && !!el.closest?.(REPLY_BUTTON_SELECTOR);
}

function anchorReplyComposer() {
    return {
        open: false,
        sending: false,
        sent: false,
        error: '',
        body: '',
        anchor: { kind: '', label: '', excerpt: '', entityId: '', lang: '', thumb: '' },
        recipientId: '',
        headerTitle: '',
        errorText: '',
        changedText: '',
        sentText: '',
        notSentText: '',
        retryText: '',
        threadUrl: '',
        tmpId: '',
        tmpIdBody: null,
        drafts: {},
        // Unsent replies of closed sheets, oldest first. One toast shows at a time.
        failures: [],
        shownFailure: null,
        trigger: null,
        sendTrigger: null,
        closedByPointer: false,
        minBodyChars: 1,
        stopFocusGuard: null,

        init() {
            const data = this.$el.dataset;
            this.recipientId = String(data.recipientId || '');
            this.headerTitle = data.headerTitle || '';
            this.errorText = data.errorText || '';
            this.changedText = data.changedText || '';
            this.sentText = data.sentText || '';
            this.notSentText = data.notSentText || '';
            this.retryText = data.retryText || '';
            this.threadUrl = data.threadUrl || '';
            this.minBodyChars = Number(data.minBodyChars) || 1;
            // The photo viewer closes the sheet when the address leaves the quoted photo. The draft stays.
            window.closeAnchorReply = () => {
                if (this.open) {
                    this.close();
                }
            };
            this.stopFocusGuard = guardDialogFocus(
                () => this.open ? this.$el.querySelector('[role="dialog"]') : null,
                () => this.focusCurrentControl(),
            );
        },

        destroy() {
            this.stopFocusGuard?.();
            delete window.closeAnchorReply;
        },

        focusCurrentControl() {
            const control = this.sent ? this.$refs.viewConversation
                : this.sending ? this.$el.querySelector('[data-testid="anchor-reply-composer-close"]')
                    : this.$refs.textarea;
            control?.focus();
        },

        trapFocus(event) {
            if (this.open) trapTabFocus(this.$el.querySelector('[role="dialog"]'), event);
        },

        get normalizedBody() {
            return this.body.replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '');
        },

        // Code points, matching the mb_strlen() count of MessageAnchorService::assertBody().
        get bodyLength() {
            return [...this.normalizedBody].length;
        },

        get canSend() {
            return this.bodyLength >= this.minBodyChars;
        },

        get tooShort() {
            return this.bodyLength > 0 && !this.canSend;
        },

        openWith(detail) {
            this.trigger = detail.trigger || null;
            // The React photo viewer reads it, so its own Escape and arrow keys leave the sheet alone.
            window._anchorReplyOpen = true;
            this.hideFailureToast();
            // A send in flight keeps its draft, so its response always lands in this composer.
            if (this.sending) {
                this.open = true;
                this.$nextTick(() => this.focusCurrentControl());
                return;
            }
            const next = {
                kind: detail.kind || '',
                label: detail.label || '',
                excerpt: detail.excerpt || '',
                entityId: detail.entityId || '',
                lang: detail.lang || '',
                thumb: detail.thumb || '',
            };
            // One draft per section, so text never shows under another section and a switch never loses it.
            if (this.anchor.kind && !this.sent) {
                this.drafts[anchorKey(this.anchor)] = {
                    body: this.body, tmpId: this.tmpId, tmpIdBody: this.tmpIdBody,
                };
            }
            const key = anchorKey(next);
            this.failures = this.failures.filter((queued) => queued.key !== key);
            // A draft keeps its own key, so a retry after a lost response cannot send twice.
            const draft = this.drafts[key] || { body: '', tmpId: newTmpId(), tmpIdBody: null };
            delete this.drafts[key];
            this.anchor = next;
            this.body = draft.body;
            this.tmpId = draft.tmpId;
            this.tmpIdBody = draft.tmpIdBody;
            this.error = '';
            this.sent = false;
            this.open = true;
            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(this.$el);
                }
                // A retry from the toast starts a send before this runs, and the send disables the textarea.
                this.focusCurrentControl();
            });
        },

        close(event) {
            this.open = false;
            window._anchorReplyOpen = false;
            // A window Escape listener that runs after this one must not also close the page under the sheet.
            if (event && event.type === 'keydown') {
                event.preventDefault?.();
            }
            // A script focus inherits the keyboard state of the textarea, so a pointer close sets it explicitly.
            this.closedByPointer = !!event && event.type === 'click' && event.detail > 0;
            if (this.closedByPointer) {
                this.$refs.textarea?.blur();
            }
            const trigger = this.trigger;
            const focusOptions = this.closedByPointer ? { focusVisible: false } : undefined;
            // The queue shows after focus returns, so its focus check reads the restored state.
            this.$nextTick(() => {
                trigger?.focus(focusOptions);
                if (!this.open && !this.shownFailure) {
                    this.showNextFailure();
                }
            });
        },

        async send() {
            const body = this.normalizedBody;
            if (!this.canSend || this.sending || this.sent) {
                return;
            }
            // A key already sent with other text would return that message as a duplicate.
            if (this.tmpIdBody !== null && this.tmpIdBody !== body) {
                this.tmpId = newTmpId();
            }
            this.tmpIdBody = body;
            this.sendTrigger = this.trigger;
            this.sending = true;
            this.error = '';
            try {
                const failure = await withSendDeadline(async (signal) => {
                    const res = await postAnchoredMessage({
                        recipientId: this.recipientId,
                        kind: this.anchor.kind,
                        entityId: this.anchor.entityId,
                        lang: this.anchor.lang,
                        excerpt: this.anchor.excerpt,
                        body,
                        tmpId: this.tmpId,
                        signal,
                    });
                    const gate = window.PhoneGateRefusal;
                    if (gate && await gate.followApi(res)) {
                        return { gated: true };
                    }
                    if (!res.ok) {
                        return { message: await anchoredSendErrorMessage(res, this.errorText, this.changedText) };
                    }
                    return null;
                });
                if (failure && failure.gated) {
                    this.sending = false;
                    return;
                }
                if (failure) {
                    this.failSend(failure.message);
                    return;
                }

                this.sent = true;
                this.sending = false;
                if (!this.open) {
                    window.showSuccess?.(this.sentText);
                    return;
                }
                this.$nextTick(() => {
                    if (window.LucideIcons) {
                        window.LucideIcons.initialize(this.$el);
                    }
                    if (this.open && this.$refs.viewConversation) {
                        this.$refs.viewConversation.focus();
                    }
                });
            } catch {
                this.failSend(this.errorText);
            }
        },

        failSend(message) {
            this.error = message;
            this.sending = false;
            if (this.open) {
                this.$nextTick(() => {
                    if (this.$refs.textarea) {
                        this.$refs.textarea.focus();
                    }
                });
                return;
            }
            // A closed sheet reports the failure in a toast, so an unsent reply never passes for a sent one.
            this.reportFailure({
                key: anchorKey(this.anchor),
                anchor: { ...this.anchor },
                trigger: this.sendTrigger,
                tmpId: this.tmpId,
                body: this.tmpIdBody,
                message,
            });
        },

        reportFailure(failure) {
            const index = this.failures.findIndex((queued) => queued.key === failure.key);
            if (index === -1) {
                this.failures.push(failure);
            } else {
                this.failures[index] = failure;
            }
            const shown = this.shownFailure;
            if (shown && shown.key === failure.key) {
                this.shownFailure = null;
                shown.hidden = true;
                shown.handle?.dismiss();
            }
            if (!this.shownFailure) {
                this.showNextFailure();
            }
        },

        showNextFailure() {
            const failure = this.failures[0];
            if (!failure) {
                this.shownFailure = null;
                return;
            }
            // A changed section fails the same way on every retry, so its toast names the reason instead.
            const retryable = failure.message !== this.changedText;
            this.shownFailure = failure;
            failure.handle = window.showActionToast?.(retryable ? this.notSentText : failure.message, {
                duration: FAILURE_TOAST_MS,
                actionLabel: retryable ? this.retryText : undefined,
                // Checked at display, so a user who moved on to the pointer keeps focus.
                focusAction: hasKeyboardFocus(failure.focusFrom) || (!this.closedByPointer && hasKeyboardFocusOnReplyButton()),
                closeOnEscape: true,
                onAction: () => this.retryFailure(failure),
                onClose: () => {
                    if (failure.hidden) {
                        failure.hidden = false;
                        return;
                    }
                    // A keyboard user inside the toast returns to the reply button, not to the end of the page.
                    const returnTo = [failure.trigger, this.trigger].find((button) => button?.isConnected);
                    if (returnTo && failure.handle?.element?.contains(document.activeElement)) {
                        this.closedByPointer = false;
                        returnTo.focus();
                    }
                    this.failures = this.failures.filter((queued) => queued !== failure);
                    if (this.shownFailure === failure) {
                        this.showNextFailure();
                    }
                },
            });
        },

        retryFailure(failure) {
            if (this.sending) {
                this.reportFailure({ ...failure, focusFrom: document.activeElement });
                return;
            }
            this.openWith({ ...failure.anchor, trigger: failure.trigger });
            // Retry sends only the draft that failed, never text written or sent after it.
            if (this.tmpId === failure.tmpId && this.normalizedBody === failure.body) {
                this.send();
            }
        },

        // The toast region sits above the sheet and outside its modal. The failure stays queued and shows again on close().
        hideFailureToast() {
            const shown = this.shownFailure;
            if (!shown) {
                return;
            }
            this.shownFailure = null;
            shown.hidden = true;
            shown.handle?.dismiss();
        },
    };
}

document.addEventListener('alpine:init', () => {
    if (window.Alpine) {
        window.Alpine.data('anchorReplyComposer', anchorReplyComposer);
    }
});
